import { UIEvent, useRef, useState } from 'react';
import FastPurchaseImage from '@/assets/img/fast-purchase.svg';
import ModalDefault, {
  IModalDefaultProps,
} from '@/components/Modals/ModalDefault';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { ILocation, LocationType } from '@/types/api/locations';
import { ApiPagination } from '@/types/api/pagination';
import { getLocationLabel } from '@/utils';
import { handleErrorAsync } from '@/utils/handlers';
import { removeFalsyValues } from '@/utils/objectUtils';
import { InfoOutlined } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';

const CITY_LOCATIONS_PAGE_SIZE = 50 as const;

interface CompanyShippingLocationsSelectionProps {
  selectedLocations: ILocation[];
  handleChangeLocations: (newLocations: ILocation[]) => void;
}

const locationGroupTitle = { [LocationType.CITY]: 'Cidades' };

const CustomTextField = (props: TextFieldProps) => {
  return (
    <TextField
      {...props}
      variant="standard"
      placeholder="Pesquise..."
      name="locations"
      sx={{
        '& .MuiAutocomplete-endAdornment': { top: 'calc(100% - 32px)' },
        '.MuiInputBase-root': { paddingBottom: 6 },
        '& .MuiInputBase-input': {
          position: 'absolute',
          bottom: '6px',
          left: '4px',
          width: '100% !important',
        },
      }}
    />
  );
};

const AboutFastPurchaseModal = (props: IModalDefaultProps) => {
  const { onClose, ...otherProps } = props;
  return (
    <ModalDefault
      {...otherProps}
      src={FastPurchaseImage}
      alt="Imagem representando compra rápida."
      title="Compra rápida"
      textButton="Entendi"
      onClickButton={onClose}
      onClose={onClose}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'left',
          gap: 2,
          mb: 5,
          mt: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <InfoOutlined sx={{ color: 'text.secondary' }} />

          <Typography variant="body2">
            Compra rápida é uma funcionalidade para pedidos de compradores
            locais, que desejam ter uma entrega rápida dos itens.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <InfoOutlined sx={{ color: 'text.secondary' }} />

          <Typography variant="body2">
            Para estar disponível aos compradores, selecione as cidades onde
            poderá efetuar a entrega rápida.
          </Typography>
        </Box>
      </Box>
    </ModalDefault>
  );
};

function CompanyShippingLocationsSelection(
  props: CompanyShippingLocationsSelectionProps
) {
  const hasMoreLocations = useRef(true);
  const { selectedLocations, handleChangeLocations } = props;

  const controller = useRef<AbortController>(new AbortController());
  const [openFastPurchaseModal, setOpenFastPurchaseModal] = useState(false);
  const [locations, setLocations] = useState(selectedLocations);
  const [pageCount, setPageCount] = useState(1);
  const [isFetching, setIsFetching] = useState(false);
  const [search, setSearch] = useState('');

  const handleOpenOptions = async () => {
    const newLocations = await getLocationsByQueryParams({
      page: pageCount,
      search,
    });

    if (newLocations) updateLocations(newLocations);
  };

  const handleChangeInput = async (value: string) => {
    setSearch(value);
    getLocationBySearch(value);
  };

  const handleOpenModal = () => setOpenFastPurchaseModal(true);

  async function getLocationsByQueryParams(params: ObjectLiteral) {
    controller.current.abort();
    controller.current = new AbortController();
    const signal = controller.current.signal;

    const updatedParams = removeFalsyValues(params);

    setIsFetching(true);
    const [apiLocations, error] = await handleErrorAsync(async () => {
      const { data } = await api.get<ApiPagination<ILocation>>(
        'api/locations/',
        {
          params: {
            type: LocationType.CITY,
            page_size: CITY_LOCATIONS_PAGE_SIZE,
            ordering: '-created_at',
            ...updatedParams,
          },
          signal: signal,
        }
      );

      hasMoreLocations.current = !!data.next;

      return data.results;
    }).finally(() => setIsFetching(false));

    if (signal.aborted) return;

    if (error) {
      Toast('error', error.message);
      return;
    }

    return apiLocations;
  }

  async function getLocationBySearch(search: string) {
    const newLocations = await getLocationsByQueryParams({
      search,
    });

    setPageCount(1);
    if (newLocations) setLocations(newLocations);
  }

  const handleScrollListBox = async (event: UIEvent<HTMLUListElement>) => {
    const listBoxEl = event.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = listBoxEl;
    const isBoxBottom = scrollTop >= scrollHeight - clientHeight - 2;

    if (isBoxBottom && hasMoreLocations.current && !isFetching) {
      const updatedPageCount = pageCount + 1;
      const locationsUpdated = await getLocationsByQueryParams({
        page: updatedPageCount,
        search,
      });

      if (locationsUpdated) updateLocations(locationsUpdated);

      setPageCount(updatedPageCount);
      listBoxEl.scrollTo(0, scrollTop);
      return;
    }
  };

  function updateLocations(allLocations: ILocation[]) {
    const newLocations = allLocations.filter(
      newLocation => !locations.some(({ id }) => id === newLocation.id)
    );
    setLocations(prevState => [...prevState, ...newLocations]);
  }

  return (
    <Box component="section">
      <Box sx={{ display: 'grid', gap: '4px', marginBottom: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography component="h3" variant="h6" color="text.secondary">
            Locais de entrega
          </Typography>

          <Typography
            color="primary.main"
            variant="body2"
            sx={{
              textDecoration: 'underline',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
            onClick={handleOpenModal}
          >
            Compra rápida! Saiba mais
          </Typography>
        </Box>

        <Typography color="text.secondary" variant="caption">
          Nome da cidade que a empresa atua
        </Typography>
      </Box>

      <Autocomplete
        value={selectedLocations}
        loadingText="Pesquisando..."
        noOptionsText={isFetching ? 'Pesquisando...' : 'Não encontrado'}
        onOpen={handleOpenOptions}
        componentsProps={{ popper: { id: 'location-listbox' } }}
        onChange={(_, value) => handleChangeLocations(value)}
        options={locations}
        getOptionLabel={option => getLocationLabel(option)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        groupBy={() => locationGroupTitle[LocationType.CITY]}
        ChipProps={{ sx: { backgroundColor: '#E3E3E3' } }}
        ListboxProps={{ onScroll: handleScrollListBox }}
        renderInput={params => <CustomTextField {...params} />}
        onInputChange={(_, value) => handleChangeInput(value)}
        inputValue={search}
        renderOption={(props, option, { selected }) => {
          const isLastElement =
            option.id === locations[locations.length - 1].id;

          return (
            <Box key={option.id}>
              <li {...props} data-testid={option.id}>
                <Checkbox checked={selected} /> {getLocationLabel(option)}
              </li>

              {isLastElement && isFetching && (
                <Box marginY={2}>
                  <CircularProgress
                    sx={{ display: 'flex', margin: '0 auto' }}
                    size={20}
                  />
                </Box>
              )}
            </Box>
          );
        }}
        disableCloseOnSelect
        disablePortal
        autoHighlight
        fullWidth
        multiple
      />

      <AboutFastPurchaseModal
        isOpen={openFastPurchaseModal}
        onClose={() => setOpenFastPurchaseModal(false)}
      />
    </Box>
  );
}

export default CompanyShippingLocationsSelection;
export type { CompanyShippingLocationsSelectionProps };
